import { Shipment } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { CartItemWrapper, OrderCell, OrderItemWrapper, SmartLink, Title } from '@components/common';
import { Box, Button, styled } from '@mui/material';
import { breakpoints, palette } from '@themes/theme.helpers';
import { t } from '@translations/translation';
import { shortDate } from '@utils/date.utils';

// STYTLES
//---------

const OrderContentHeaderWrapper = styled(Box)`
  background-color: white;
  margin: 0 0 4px 0;
  .order-item-wrapper {
    justify-content: left;
    align-items: flex-end;
    font-weight: 700;
    padding: 0;
  }
  & > * {
    height: auto;
  }
`;

export const LineItemColumnNames = styled(OrderItemWrapper)`
  height: 0;
  overflow: hidden;
  border-bottom: 2px solid ${palette.primary('main')};
  padding: 0;

  & > div {
    height: 60px;
  }

  ${breakpoints.up('lg')} {
    height: auto;
  }
`;

export const CartLineItemColumnNames = styled(CartItemWrapper)`
  height: 0;
  overflow: hidden;
  padding: 0;
  height: auto;
  & > div {
    height: 60px;
  }
`;

const OrderContentTitle = styled(Box)`
  padding: 16px 16px 0 16px;
  ${breakpoints.up('lg')} {
    padding: 40px 40px 0 40px;
  }
`;

const OrderDeliveryInfo = styled(Box)`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  ${breakpoints.up('lg')} {
    padding: 40px 40px 20px 40px;
    flex-direction: row;
  }
`;

const DeliveryDate = styled(Box)`
  text-transform: uppercase;
  font-weight: 700;
`;

const TrackingNumber = styled(Box)`
  display: flex;
  flex-direction: row;
  color: #4f4f4f;
  gap: 40px;
  align-items: center;
  .bold {
    font-weight: 700;
    color: black;
  }
  .number {
    margin-left: 8px;
  }
`;

export type OrderContentHeaderProps = {
  hasActions?: boolean;
  expectedDeliveryDate?: Shipment['expectedDeliveryDate'];
  trackingNumber?: Shipment['trackingNumber'];
  shipmentName?: Shipment['name'];
  trackingLink?: Shipment['trackingLink'];
};

export const OrderContentHeader = ({
  trackingNumber,
  expectedDeliveryDate,
  hasActions,
  shipmentName,
  trackingLink,
}: OrderContentHeaderProps) => {
  return (
    <OrderContentHeaderWrapper>
      <OrderContentTitle>
        <Title variant='h3' noDot>
          {shipmentName || t(TextCodes.Order.Details.OrderDeliveryHeader)}
        </Title>
      </OrderContentTitle>
      <OrderDeliveryInfo>
        {expectedDeliveryDate && (
          <DeliveryDate>
            {t(TextCodes.Order.Details.ExpectedDeliveryTime)} {shortDate(expectedDeliveryDate)}
          </DeliveryDate>
        )}
        {trackingNumber && (
          <TrackingNumber>
            <Box>
              {t(TextCodes.Order.Details.TrackingNumber)}
              <Box className='number bold' component='span'>
                {trackingNumber}
              </Box>
            </Box>
            <SmartLink href={trackingLink || ''} target='_blank'>
              <Button variant='ghost'>{t(TextCodes.Order.Details.TrackOrder)}</Button>
            </SmartLink>
          </TrackingNumber>
        )}
      </OrderDeliveryInfo>
      <LineItemColumnNames className='order-item-wrapper header'>
        <OrderCell></OrderCell>
        <OrderCell className='long'>{t(TextCodes.Order.Details.ProductColumn)}</OrderCell>
        <OrderCell>{t(TextCodes.Order.Details.UnitPriceColumn)}</OrderCell>
        <OrderCell>{t(TextCodes.Order.Details.DiscountColumn)}</OrderCell>
        <OrderCell>{t(TextCodes.Order.Details.NetPriceColumn)}</OrderCell>
        <OrderCell>{t(TextCodes.Order.Details.QuantityColumn)}</OrderCell>
        <OrderCell>{t(TextCodes.Order.Details.TotalColumn)}</OrderCell>
        {hasActions && <OrderCell className='actions'></OrderCell>}
      </LineItemColumnNames>
    </OrderContentHeaderWrapper>
  );
};
